import { useState, useEffect } from 'react';
import { Heart, ChatCircle, Trophy } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { usernameColorClass } from '../lib/usernameColors';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ThoughtCard({ thought, onUpdate, showBadge, badgeLabel, badgeGift }) {
  const [liked, setLiked] = useState(thought.liked_by_me || false);
  const [likes, setLikes] = useState(thought.likes || 0);
  const [comments, setComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  const [commentsCount, setCommentsCount] = useState(thought.comments_count || 0);
  const [loading, setLoading] = useState(false);

  const handleLike = async () => {
    try {
      const { data } = await axios.post(
        `${API}/thoughts/${thought.id}/like`,
        {},
        { withCredentials: true }
      );
      setLiked(data.liked);
      setLikes(data.likes);
      if (onUpdate) onUpdate();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to like thoughts');
      } else {
        toast.error('Failed to like');
      }
    }
  };

  const fetchComments = async () => {
    try {
      const { data } = await axios.get(`${API}/thoughts/${thought.id}/comments`);
      setComments(data);
    } catch (error) {
      // Silent fail
    }
  };

  const toggleComments = () => {
    if (!showComments) fetchComments();
    setShowComments(!showComments);
  };

  const submitComment = async () => {
    if (!commentInput.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/thoughts/${thought.id}/comments`,
        { content: commentInput },
        { withCredentials: true }
      );
      setComments([...comments, data]);
      setCommentsCount(commentsCount + 1);
      setCommentInput('');
      toast.success('Comment posted!');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to comment');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to post comment');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={`p-6 ${showBadge ? 'border-primary shadow-[0_0_20px_rgba(225,29,72,0.2)]' : ''}`} data-testid={`thought-card-${thought.id}`}>
      {showBadge && (
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-primary/30">
          <div className="flex items-center space-x-2">
            <Trophy size={20} weight="fill" className="text-primary" />
            <span className="uppercase text-xs tracking-[0.2em] font-medium text-primary">{badgeLabel}</span>
          </div>
          {badgeGift && (
            <span className="text-xs text-muted-foreground" data-testid="badge-gift">
              Prize: <span className="text-primary font-medium">{badgeGift}</span>
            </span>
          )}
        </div>
      )}

      <h4 className="text-xl tracking-tight font-light mb-2" data-testid={`thought-title-${thought.id}`}>{thought.title}</h4>
      <p className="text-sm text-muted-foreground mb-4" data-testid={`thought-content-${thought.id}`}>{thought.content}</p>

      <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
        <div data-testid={`thought-author-${thought.id}`}>
          By:{' '}
          {thought.is_anonymous ? (
            <span className="text-primary">{thought.author_name}</span>
          ) : (
            <Link
              to={`/u/${thought.author_name}`}
              className={`hover:underline ${usernameColorClass(thought.author_color)}`}
              data-testid={`thought-author-link-${thought.id}`}
            >
              {thought.author_name}
            </Link>
          )}
        </div>
        <div>{new Date(thought.created_at).toLocaleDateString()}</div>
      </div>

      <div className="flex items-center space-x-2 pt-3 border-t border-border">
        <Button
          onClick={handleLike}
          variant="ghost"
          size="sm"
          className="rounded-full text-xs"
          data-testid={`thought-like-button-${thought.id}`}
        >
          <Heart size={16} weight={liked ? 'fill' : 'regular'} className={liked ? 'text-primary mr-1' : 'mr-1'} />
          <span data-testid={`thought-likes-${thought.id}`}>{likes}</span>
        </Button>

        <Button
          onClick={toggleComments}
          variant="ghost"
          size="sm"
          className="rounded-full text-xs"
          data-testid={`thought-comment-button-${thought.id}`}
        >
          <ChatCircle size={16} className="mr-1" />
          <span data-testid={`thought-comments-count-${thought.id}`}>{commentsCount}</span>
        </Button>
      </div>

      {showComments && (
        <div className="mt-4 pt-4 border-t border-border" data-testid={`thought-comments-section-${thought.id}`}>
          <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
            {comments.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">No comments yet. Be the first!</p>
            ) : (
              comments.map((c) => (
                <div key={c.id} className="text-xs p-2 bg-secondary/50" data-testid={`comment-${c.id}`}>
                  <span className="font-medium text-primary">{c.username}: </span>
                  <span className="text-muted-foreground">{c.content}</span>
                </div>
              ))
            )}
          </div>
          <div className="flex space-x-2">
            <Textarea
              placeholder="Write a comment..."
              value={commentInput}
              onChange={(e) => setCommentInput(e.target.value)}
              rows={2}
              className="text-sm"
              data-testid={`comment-input-${thought.id}`}
            />
            <Button
              onClick={submitComment}
              size="sm"
              className="rounded-full self-end"
              disabled={loading || !commentInput.trim()}
              data-testid={`comment-submit-${thought.id}`}
            >
              Post
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
